import React, { useContext, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTypes, Button, Drawer, Icon, useDrawer } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import {
	getListApplicationRegistrationGroupsQueryKey,
	getListApplicationRegistrationsGroupedQueryKey,
} from '@/generated/application-registrations';
import {
	ApplicationRegistrationId,
	LocationDetailsResponse,
	LocationRequest,
	PageType,
	RegistrationCompositeId,
} from '@/generated/data.schemas';
import { useGetLayout } from '@/generated/layout';
import { useCreateCompositeWithLocation, useCreateLocation } from '@/generated/location';
import { getGetRegistrationCompositeQueryKey } from '@/generated/registration-composite';
import { useGetTranslations } from '@/generated/translations';
import { isHttpValidationProblemDetails } from '@/helpers';
import { useNotificationStore } from '@/store/notificationStore';
import { LocationForm } from '../shared/LocationForm';
import styles from './RegisterLocation.module.scss';

interface RegisterLocationProps {
	accountId: number;
	registrationCompositeId?: RegistrationCompositeId;
	applicationId?: ApplicationRegistrationId;
}

const RegisterLocation: React.FunctionComponent<RegisterLocationProps> = ({
	accountId,
	registrationCompositeId,
	applicationId,
}) => {
	const ariaControlId = 'register-location';
	const formId = 'register-location-form';
	const { segmentationId } = useContext(SegmentationContext);
	const addNotification = useNotificationStore((state) => state.addNotification);
	const queryClient = useQueryClient();
	const { data: layout } = useGetLayout(segmentationId);
	const { data: translations } = useGetTranslations(segmentationId);
	const drawerRef = useRef<HTMLDivElement>(null);
	const { isOpen, isRendered, handleOpen, handleClose } = useDrawer(drawerRef);

	const path = useMemo(() => {
		return layout?.data.staticLinks.find((link) => link.pageType === PageType.RegistrationComposite)?.url;
	}, [layout]);

	// shared options used for create and update locations
	const mutationOptions = {
		onSuccess: async (x) => {
			await queryClient.invalidateQueries({
				queryKey: getListApplicationRegistrationsGroupedQueryKey(segmentationId, accountId),
			});

			// refresh groups list if new group is added
			await queryClient.invalidateQueries({
				queryKey: getListApplicationRegistrationGroupsQueryKey(segmentationId, accountId),
			});

			if (applicationId && registrationCompositeId) {
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});
			}

			addNotification({
				children: translations?.data.locations.saveSuccess,
				type: AlertTypes.SUCCESS,
			});

			if (!registrationCompositeId) {
				window.location.href = `${path}?accountid=${accountId}&registrationcompositeid=${x.data}`;
			} else {
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});
			}

			handleClose();
		},
		onError: (axiosError) => {
			const error = axiosError.response?.data;
			if (!error) {
				console.error('Unknown error', axiosError);
			} else if (isHttpValidationProblemDetails(error)) {
				addNotification({
					children: translations?.data.applicationRegistrations.saveFailed,
					type: AlertTypes.DANGER,
				});

				setValidationErrors(error.errors);
			} else {
				addNotification({
					children: translations?.data.shared.genericErrorMsg,
					type: AlertTypes.DANGER,
				});
			}
		},
	};

	const useCreateLocationMutation = useCreateLocation({
		mutation: mutationOptions,
	});

	const useCreateCompositeWithLocationMutation = useCreateCompositeWithLocation({
		mutation: mutationOptions,
	});

	const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);

	const handleSubmit = (data: LocationRequest) => {
		setValidationErrors(null);

		if (registrationCompositeId) {
			useCreateLocationMutation.mutate({
				segmentationId,
				accountId,
				registrationCompositeId,
				data,
			});
		} else {
			useCreateCompositeWithLocationMutation.mutate({
				segmentationId,
				accountId,
				data,
			});
		}
	};

	return (
		<>
			<Button onClick={handleOpen}>
				{translations?.data.locations.register}
				<Icon name={'plus'} size={'md'} />
			</Button>
			{isRendered && (
				<Drawer
					drawerRef={drawerRef}
					isOpen={isOpen}
					id={ariaControlId}
					desktopWidth={'400px'}
					direction={'Right'}
				>
					<Drawer.Header title={translations?.data.locations.register} handleClose={handleClose} />

					<Drawer.Body>
						<LocationForm
							handleSubmit={handleSubmit}
							validationErrors={validationErrors}
							formId={formId}
							location={{} as LocationDetailsResponse}
							accountId={accountId}
						/>
					</Drawer.Body>

					<Drawer.Footer className={styles.footer} hasNoPadding>
						<Button variant={'ghost'} isFullWidth type={'button'} onClick={handleClose}>
							{translations?.data.shared.cancel}
						</Button>
						<Button variant={'primary'} isFullWidth type={'submit'} form={formId}>
							{translations?.data.applicationRegistrations.save}
						</Button>
					</Drawer.Footer>
				</Drawer>
			)}
		</>
	);
};

export default RegisterLocation;
